import React, { useState } from 'react';
import { Smartphone, ChevronDown, ArrowRight, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useTheme } from '../contexts/ThemeContext';
interface PhoneEntryScreenProps {
  onBack: () => void;
  onContinue: (phone: string) => void;
}
export function PhoneEntryScreen({
  onBack,
  onContinue
}: PhoneEntryScreenProps) {
  const {
    isLight
  } = useTheme();
  const [phoneNumber, setPhoneNumber] = useState('585377090');
  const [isLoading, setIsLoading] = useState(false);
  const handleContinue = () => {
    if (!phoneNumber) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onContinue(phoneNumber);
    }, 1000);
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-1354">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-1355" />

      {/* Header */}
      <div className="flex items-center mb-8 relative z-10" data-id="element-1356">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-1357">
          <ArrowLeft className="w-5 h-5" data-id="element-1358" />
        </button>
      </div>

      <div className="flex-1 flex flex-col pt-4 relative z-10" data-id="element-1359">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 border transition-colors duration-500 ${isLight ? 'bg-vexu/10 border-vexu/20 shadow-sm' : 'bg-dark-card border-white/10 shadow-xl shadow-vexu/10'}`} data-id="element-1360">
          <Smartphone className={`w-8 h-8 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1361" />
        </div>

        <h1 className={`text-3xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1362">
          Enter your number
        </h1>
        <p className={`text-base mb-8 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1363">
          We'll send you a verification code to activate your AI assistant.
        </p>

        <div className="w-full space-y-6" data-id="element-1364">
          <div className={`backdrop-blur-xl border rounded-2xl p-5 transition-colors duration-500 ${isLight ? 'bg-white/60 border-slate-200/50 shadow-sm' : 'bg-dark-card/60 border-white/10 shadow-xl shadow-black/50'}`} data-id="element-1365">
            <div className="flex gap-3" data-id="element-1366">
              <div className={`h-[52px] border rounded-xl flex items-center justify-center px-3 gap-2 backdrop-blur-lg shrink-0 transition-colors ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/5 border-white/10'}`} data-id="element-1367">
                <span className="text-xl leading-none" data-id="element-1368">🇺🇸</span>
                <span className={`font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1369">
                  +1
                </span>
                <ChevronDown className={`w-4 h-4 ml-1 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1370" />
              </div>
              <Input placeholder="(555) 123-4567" type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className="flex-1" autoFocus data-id="element-1371" />
            </div>
          </div>

          <Button fullWidth size="lg" onClick={handleContinue} loading={isLoading} disabled={!phoneNumber} icon={<ArrowRight className="w-5 h-5" data-id="element-1373" />} className="flex-row-reverse" data-id="element-1372">
            Continue
          </Button>
        </div>
      </div>

      {/* Loading Overlay */}
      <AnimatePresence data-id="element-1374">
        {isLoading && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-1375">
            <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-1376" />
            <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-1377">
              <div className="relative w-32 h-32 mb-8" data-id="element-1378">
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-1379" />
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-1380" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1381">
                  <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-1382">
                    <Smartphone className="w-10 h-10 text-vexu" data-id="element-1383" />
                  </motion.div>
                </div>
              </div>
              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1384">
                Sending Code
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1385">
                Preparing your verification...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}